import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Check, X } from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";

export default function Leaves() {
  const { user } = useAuth();
  const [tab, setTab] = useState("requests");
  const [reqs, setReqs] = useState([]);
  const [types, setTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [cal, setCal] = useState(null);

  const load = async () => {
    const [r, t, e, h, c] = await Promise.all([
      api.get("/leaves"), api.get("/leave-types"), api.get("/employees"),
      api.get("/holidays"), api.get("/calendar/team?scope=week"),
    ]);
    setReqs(r.data); setTypes(t.data); setEmployees(e.data); setHolidays(h.data); setCal(c.data);
  };
  useEffect(() => { load(); }, []);

  const decide = async (rid, status) => {
    try {
      await api.post(`/leaves/${rid}/decide`, { status });
      toast.success(`Leave ${status}`); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const addType = async () => {
    const name = prompt("Leave type name?");
    if (!name) return;
    const q = parseFloat(prompt("Annual quota (days)?") || "0");
    await api.post("/leave-types", { name, annual_quota_days: q, is_paid: true });
    toast.success("Added"); load();
  };

  const addHoliday = async () => {
    const date = prompt("Date (YYYY-MM-DD)?");
    const name = prompt("Holiday name?");
    if (!date || !name) return;
    await api.post("/holidays", { date, name });
    load();
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.employee_id, e]));
  const typeMap = Object.fromEntries(types.map((t) => [t.type_id, t]));

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight mb-1">Leaves</h1>
      <p className="text-sm text-[#A1A1AA] mb-4">Founders approve. Everyone else sees status only.</p>

      <div className="inline-flex rounded-md border border-[#262626] overflow-hidden mb-5">
        {["requests", "types", "holidays", "team"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 text-xs ${tab === t ? "bg-[#1A1A1A] text-[#F4F4F5]" : "text-[#A1A1AA]"}`} data-testid={`leaves-tab-${t}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "requests" && (
        <div className="tt-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0F0F0F]">
              <tr className="text-[#71717A] text-xs font-mono">
                <th className="text-left px-4 py-2">Employee</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Dates</th>
                <th className="text-right px-4 py-2">Days</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {reqs.map((r) => (
                <tr key={r.request_id} className="border-t border-[#1E1E1E]">
                  <td className="px-4 py-3">{empMap[r.employee_id]?.display_name || r.employee_id}</td>
                  <td className="px-4 py-3">{typeMap[r.type_id]?.name || "?"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.start_date} → {r.end_date}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.days}</td>
                  <td className="px-4 py-3">
                    {r.status === "pending" || r.status === "pending_review" ? <span className="tt-badge-warn">{r.status}</span>
                      : r.status === "approved" ? <span className="tt-badge-pass">approved</span>
                      : <span className="tt-badge-danger">{r.status}</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {(r.status === "pending" || r.status === "pending_review") && user?.role === "super_admin" && (
                      <div className="inline-flex gap-1">
                        <button onClick={() => decide(r.request_id, "approved")} className="tt-btn-ghost text-[#22C55E]" data-testid={`leave-approve-${r.request_id}`}><Check size={14} /></button>
                        <button onClick={() => decide(r.request_id, "rejected")} className="tt-btn-ghost text-[#EF4444]"><X size={14} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {reqs.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#71717A]">No leave requests.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "types" && (
        <div className="tt-card p-5">
          <div className="flex justify-between mb-4">
            <div className="tt-label">Configured leave types</div>
            <button onClick={addType} className="tt-btn-primary text-xs flex items-center gap-1"><Plus size={12} /> Add</button>
          </div>
          <div className="space-y-2">
            {types.map((t) => (
              <div key={t.type_id} className="flex items-center justify-between py-2 border-b border-[#1E1E1E] last:border-0">
                <div><span className="font-medium">{t.name}</span> <span className="font-mono text-xs text-[#71717A] ml-2">{t.annual_quota_days} days · {t.accrual}</span></div>
                <div className="flex gap-1">{t.is_paid ? <span className="tt-badge-pass">Paid</span> : <span className="tt-badge-warn">Unpaid (LOP)</span>}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "holidays" && (
        <div className="tt-card p-5">
          <div className="flex justify-between mb-4">
            <div className="tt-label">Company holiday calendar</div>
            <button onClick={addHoliday} className="tt-btn-primary text-xs flex items-center gap-1"><Plus size={12} /> Add</button>
          </div>
          <div className="space-y-1 font-mono text-sm">
            {holidays.map((h) => <div key={h.holiday_id} className="flex gap-4 border-b border-[#1E1E1E] py-2 last:border-0"><span className="text-[#A1A1AA]">{h.date}</span><span>{h.name}</span></div>)}
          </div>
        </div>
      )}

      {tab === "team" && cal && (
        <div className="tt-card p-5">
          <div className="tt-label mb-3">Week of {cal.start}</div>
          <div className="space-y-2 text-sm">
            <div><span className="tt-badge-info">On leave</span> {cal.leaves.length === 0 && <span className="text-[#71717A] ml-2">nobody</span>}</div>
            {cal.leaves.map((l) => <div key={l.request_id} className="ml-3">{empMap[l.employee_id]?.display_name || l.employee_id}: <span className="font-mono text-xs">{l.start_date} → {l.end_date}</span></div>)}
            <div className="pt-3"><span className="tt-badge-warn">Unplanned absences</span> {cal.absences.length === 0 && <span className="text-[#71717A] ml-2">none</span>}</div>
            {cal.absences.map((a) => <div key={a.absence_id} className="ml-3">{empMap[a.employee_id]?.display_name}: <span className="font-mono text-xs">{a.date}</span> — {a.reason || "no reason given"}</div>)}
            <div className="pt-3"><span className="tt-badge-pass">Holidays</span> {cal.holidays.length === 0 && <span className="text-[#71717A] ml-2">none</span>}</div>
            {cal.holidays.map((h) => <div key={h.holiday_id} className="ml-3 font-mono text-xs">{h.date} — {h.name}</div>)}
          </div>
        </div>
      )}
    </div>
  );
}
